'use client';

import { c as _c } from "react/compiler-runtime";
import { useLocale } from 'next-intl';
import { useMemo } from 'react';
import useSWR from 'swr';
import useSWRInfinite from 'swr/infinite';
import { EXPLORE_VIVERSE_CONTENT_TYPES } from '@/constants/explore-viverse';
import { SEARCH_PARAM_KEYS } from '@/constants/search';
import { useUAContext } from '@/context/UAContext';
import { useBrowserLocale } from '@/hooks/useBrowserLocale';
import { skeletonState, useSavedPages, useScrollRestorationSWR } from '@/hooks/useScrollRestoration';
import { getLandingGameScore } from '@/lib/configs';
import { adaptDeviceType, convertToDeviceType, getApiContentTypes, getApiDevice, getApiSort, getDeviceValue, isGamesOnlyContentTypes, mapExploreRoomItemToRoom } from '@/lib/search';
import { Locale } from '@/server/constants/locales';
import { exploreRooms, ExploreRoomsQueryParams, getRecommendWorlds, getRoomTagRecommendWorlds, PaginatedResponse, Room } from '@/services/cms';
import { WorldTypeValue } from '@/types/search';
export const EXPLORE_VIVERSE_LIMIT = 20;
type ExploreViversePage = PaginatedResponse<Partial<Room>>;
type UseExploreViverseDataParams = {
  activeTag: string;
  htcToken?: string;
  sort: string | null;
  device: string | null;
  type: WorldTypeValue | null;
};
const getKey = (pageIndex: number, previousPageData: ExploreViversePage | null, activeTag: string, sort: string | null, device: string | null, type: string | null, browserLocale: string) => {
  if (!browserLocale) return null;
  const params = new URLSearchParams({
    [SEARCH_PARAM_KEYS.GENRE]: activeTag,
    start: '0'
  });
  if (sort) params.set(SEARCH_PARAM_KEYS.SORT, sort);
  if (device) params.set(SEARCH_PARAM_KEYS.DEVICE, device);
  if (type) params.set(SEARCH_PARAM_KEYS.TYPE, type);
  if (pageIndex === 0) return `publicRooms?${params.toString()}`;
  if (!previousPageData?.results) return null;
  const next = previousPageData.pages?.next;
  if (!next) return null;
  const query = next.split('?')[1];
  const start = new URLSearchParams(query).get('start');
  params.set('start', start || '0');
  return `publicRooms?${params.toString()}`;
};
const createFetcher = (htcToken: string | undefined, locale: Locale, browserLocale: string) => {
  return (key: string): Promise<ExploreViversePage | undefined> => {
    const query = key.split('?')[1];
    const params = new URLSearchParams(query);
    const start = Number(params.get('start'));
    const tag = params.get(SEARCH_PARAM_KEYS.GENRE);
    const sort = params.get(SEARCH_PARAM_KEYS.SORT);
    const device = params.get(SEARCH_PARAM_KEYS.DEVICE);
    const type = params.get(SEARCH_PARAM_KEYS.TYPE) as WorldTypeValue | null;
    const apiSort = getApiSort(sort, false);
    const apiDevice = getApiDevice(device);
    const apiContentTypes = getApiContentTypes(type) ?? EXPLORE_VIVERSE_CONTENT_TYPES;
    const requestParams: ExploreRoomsQueryParams = {
      limit: EXPLORE_VIVERSE_LIMIT,
      start,
      site_locale: locale,
      locale: browserLocale,
      tag: tag === 'all-experiences' ? '' : tag || undefined,
      exclude_persona: true,
      sort: apiSort,
      ...(apiDevice && {
        device: apiDevice
      }),
      content_types: apiContentTypes,
      ...(isGamesOnlyContentTypes(apiContentTypes) && {
        game_score: getLandingGameScore()
      })
    };
    return exploreRooms(requestParams, htcToken, false).then(res => {
      if (!res?.results) return undefined;
      return {
        pages: res.results.pages,
        results: res.results.results.map(mapExploreRoomItemToRoom)
      };
    });
  };
};
const createFetcherRoomTagRecommendWorlds = (htcToken: string | undefined, locale: Locale) => {
  return (key: string): Promise<ExploreViversePage | undefined> | undefined => {
    const query = key.split('?')[1];
    const params = new URLSearchParams(query);
    const start = Number(params.get('start'));
    const tag = params.get(SEARCH_PARAM_KEYS.GENRE);
    const device = params.get(SEARCH_PARAM_KEYS.DEVICE);
    const type = params.get(SEARCH_PARAM_KEYS.TYPE) as WorldTypeValue | null;
    const apiDevice = getApiDevice(device);
    const apiContentTypes = getApiContentTypes(type) ?? EXPLORE_VIVERSE_CONTENT_TYPES;
    if (!tag) return undefined;
    const requestParams: Parameters<typeof getRoomTagRecommendWorlds>[1] = {
      limit: EXPLORE_VIVERSE_LIMIT,
      start,
      locale,
      ...(apiDevice && {
        device: apiDevice
      }),
      content_types: apiContentTypes
    };
    return getRoomTagRecommendWorlds(tag, requestParams, htcToken, false).then(res => res?.results);
  };
};
export function useExploreViverseData(t0) {
  const $ = _c(58);
  const {
    activeTag,
    htcToken,
    sort,
    device,
    type
  } = t0;
  const locale = useLocale();
  const {
    deviceTypeWithAndroidAndIOS,
    isIosMobileApp
  } = useUAContext();
  let t1;
  if ($[0] !== device || $[1] !== deviceTypeWithAndroidAndIOS) {
    t1 = device ?? getDeviceValue(deviceTypeWithAndroidAndIOS) ?? "desktop";
    $[0] = device;
    $[1] = deviceTypeWithAndroidAndIOS;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const effectiveDevice = t1;
  const browserLocale = useBrowserLocale();
  const saved = useSavedPages();
  const sortByRecommend = sort === "recommended" || !sort;
  const enableRecommendWorlds = activeTag === "all-experiences" && sortByRecommend;
  let t2;
  if ($[3] !== activeTag || $[4] !== effectiveDevice || $[5] !== enableRecommendWorlds || $[6] !== locale || $[7] !== type) {
    t2 = enableRecommendWorlds ? [activeTag, effectiveDevice, type, locale, "recommend-worlds"] : null;
    $[3] = activeTag;
    $[4] = effectiveDevice;
    $[5] = enableRecommendWorlds;
    $[6] = locale;
    $[7] = type;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== deviceTypeWithAndroidAndIOS || $[10] !== effectiveDevice || $[11] !== htcToken || $[12] !== locale || $[13] !== type) {
    t3 = () => {
      const apiDevice = getApiDevice(effectiveDevice);
      if (!apiDevice) {
        return null;
      }
      const deviceType = convertToDeviceType(apiDevice);
      const adaptedDeviceType = adaptDeviceType(deviceType, deviceTypeWithAndroidAndIOS);
      const apiContentTypes = getApiContentTypes(type) ?? EXPLORE_VIVERSE_CONTENT_TYPES;
      return getRecommendWorlds(htcToken, {
        locale,
        device: adaptedDeviceType,
        content_types: apiContentTypes
      }).then(_temp);
    };
    $[9] = deviceTypeWithAndroidAndIOS;
    $[10] = effectiveDevice;
    $[11] = htcToken;
    $[12] = locale;
    $[13] = type;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  let t4;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      revalidateOnFocus: false
    };
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  const {
    data: recommendWorlds
  } = useSWR(t2, t3, t4);
  let t5;
  if ($[16] !== activeTag || $[17] !== browserLocale || $[18] !== effectiveDevice || $[19] !== sort || $[20] !== type) {
    t5 = (pageIndex, previousPageData) => getKey(pageIndex, previousPageData, activeTag, sort, effectiveDevice, type, browserLocale);
    $[16] = activeTag;
    $[17] = browserLocale;
    $[18] = effectiveDevice;
    $[19] = sort;
    $[20] = type;
    $[21] = t5;
  } else {
    t5 = $[21];
  }
  let t6;
  if ($[22] !== activeTag || $[23] !== browserLocale || $[24] !== htcToken || $[25] !== locale || $[26] !== sortByRecommend) {
    t6 = activeTag !== "all-experiences" && sortByRecommend && !activeTag.includes(",") ? createFetcherRoomTagRecommendWorlds(htcToken, locale) : createFetcher(htcToken, locale, browserLocale);
    $[22] = activeTag;
    $[23] = browserLocale;
    $[24] = htcToken;
    $[25] = locale;
    $[26] = sortByRecommend;
    $[27] = t6;
  } else {
    t6 = $[27];
  }
  let t7;
  if ($[28] !== saved.savedPages) {
    t7 = {
      revalidateFirstPage: false,
      initialSize: saved.savedPages
    };
    $[28] = saved.savedPages;
    $[29] = t7;
  } else {
    t7 = $[29];
  }
  const {
    data,
    isLoading,
    isValidating,
    size,
    setSize
  } = useSWRInfinite(t5, t6, t7);
  let t8;
  if ($[30] !== isLoading || $[31] !== isValidating || $[32] !== setSize || $[33] !== size) {
    t8 = {
      size,
      setSize,
      isLoading,
      isValidating
    };
    $[30] = isLoading;
    $[31] = isValidating;
    $[32] = setSize;
    $[33] = size;
    $[34] = t8;
  } else {
    t8 = $[34];
  }
  const trackedSetSize = useScrollRestorationSWR(saved, t8);
  let t9;
  if ($[35] !== data || $[36] !== enableRecommendWorlds || $[37] !== isIosMobileApp || $[38] !== recommendWorlds) {
    const rooms = data?.flatMap(_temp2).filter(_temp3) || [];
    const combined = enableRecommendWorlds ? [...(recommendWorlds || []), ...rooms].filter(_temp4) : rooms;
    t9 = isIosMobileApp ? combined.filter(_temp5) : combined;
    $[35] = data;
    $[36] = enableRecommendWorlds;
    $[37] = isIosMobileApp;
    $[38] = recommendWorlds;
    $[39] = t9;
  } else {
    t9 = $[39];
  }
  const allRooms = t9;
  const total = data?.[0]?.pages?.total || 0;
  const enableLoadMore = total > allRooms.length;
  let t10;
  if ($[40] !== allRooms.length || $[41] !== isLoading || $[42] !== isValidating || $[43] !== saved) {
    t10 = skeletonState(saved, {
      isLoading,
      isValidating
    }, allRooms.length, EXPLORE_VIVERSE_LIMIT);
    $[40] = allRooms.length;
    $[41] = isLoading;
    $[42] = isValidating;
    $[43] = saved;
    $[44] = t10;
  } else {
    t10 = $[44];
  }
  const {
    show: showSkeletons,
    count: skeletonCount
  } = t10;
  let t11;
  if ($[45] !== isLoading || $[46] !== isValidating || $[47] !== size || $[48] !== trackedSetSize) {
    t11 = {
      isLoading,
      isValidating,
      setSize: trackedSetSize,
      size
    };
    $[45] = isLoading;
    $[46] = isValidating;
    $[47] = size;
    $[48] = trackedSetSize;
    $[49] = t11;
  } else {
    t11 = $[49];
  }
  let t12;
  if ($[50] !== allRooms || $[51] !== enableLoadMore || $[52] !== enableRecommendWorlds || $[53] !== saved.isBackNav || $[54] !== showSkeletons || $[55] !== skeletonCount || $[56] !== t11) {
    t12 = {
      allRooms,
      enableLoadMore,
      enableRecommendWorlds,
      isBackNav: saved.isBackNav,
      pagination: t11,
      showSkeletons,
      skeletonCount
    };
    $[50] = allRooms;
    $[51] = enableLoadMore;
    $[52] = enableRecommendWorlds;
    $[53] = saved.isBackNav;
    $[54] = showSkeletons;
    $[55] = skeletonCount;
    $[56] = t11;
    $[57] = t12;
  } else {
    t12 = $[57];
  }
  return t12;
}
function _temp5(room_1) {
  return room_1.is_voucher_room !== true;
}
function _temp4(item, index, self) {
  return index === self.findIndex(room_0 => room_0.id === item.id);
}
function _temp3(room) {
  return room !== undefined;
}
function _temp2(page) {
  return page?.results || [];
}
function _temp(res) {
  return res?.results.rooms;
}